import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useLocation } from 'react-router-dom';
import { matchPath } from 'react-router';
import { useIntl } from 'react-intl';
import Bluebird from 'bluebird';
import { assign, orderBy } from 'lodash';
import { Steps } from 'intro.js-react';
import { GroupedVirtuoso } from 'react-virtuoso';
import {
  PanTool as PanToolIcon,
  Favorite as FavoriteIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { Box, Avatar, Typography } from '@mui/material';
import moment from 'moment';
import { Capacitor } from '@capacitor/core';
import { NoRescueCard } from '../RescuerRescuesDashboardMobileContainer';
import RescueMobileCard from '../../../../Rescue/RescueMobileCard';
import {
  filterRescuesListInfiniteScroll,
  getRescueClaimer,
  getScheduleAvailableRescues,
  isRescueClaimed,
  showAdoptRescueButton,
  showClaimRescueButton,
} from '../../../../../helpers/RescuesHelper';
import ButtonWithLoading from '../../../../../components/ButtonWithLoading';
import useRescuerDashboardActions from '../../../../../hooks/useRescuerDashboardActions';
import useRescuerRescueActions from '../../../../../hooks/useRescuerRescueActions';
import useActiveUser from '../../../../../hooks/useActiveUser';
import routes from '../../../../../routes';
import snackbarHelper from '../../../../../helpers/snackbarHelper';
import { RESCUE_BTN_ADOPTED } from '../../../../rescueAdoptConfirmation/RescueAdoptConfirmation';
import useUIFilter from '../../../../../hooks/useUIFilter';
import { RESCUER_SCHEDULE_FILTER } from '../../Schedule/ScheduleMobile';
import { initialRescuerScheduleFilters } from '../../../../../reducers/ui';
import { infiniteScheduleTour } from '../../../../../helpers/tours';
import { getRescuerSitePausedAt } from '../../../../../helpers/user';
import { Colors } from '../../../../../assets/theme/Colors';
import { StyledSubHeader } from '../myFrus/MyFrusMobile';

const RescuerScheduleMobileWithInfiniteScroll = ({ history }) => {
  const [rescuesInflight, setRescuesInflight] = useState({});
  const [toursEnabled, setToursEnabled] = useState(false);
  const location = useLocation();
  const { formatMessage } = useIntl();

  const activeUser = useActiveUser();
  const rescuesEntities = useSelector(state => state.entities.rescues);
  const { getUIFilter, setUIFilter } = useUIFilter();
  const scheduleFilters = assign(initialRescuerScheduleFilters, getUIFilter(RESCUER_SCHEDULE_FILTER));
  const { fetchScheduleTabPaginated } = useRescuerDashboardActions();
  const { rescueClaim, rescueUnadopt } = useRescuerRescueActions();

  const showInitialInfiniteScrollTour = scheduleFilters.showInitialInfiniteScrollTour.value;

  const rescuerAvailableRescues = getScheduleAvailableRescues(activeUser.id, rescuesEntities);
  const rescuesById = rescuerAvailableRescues.data.map(rescueId => rescuesEntities.byId[rescueId]);
  const filteredAvailableRescues = filterRescuesListInfiniteScroll(rescuesById, scheduleFilters);

  const { availableRescuesDayGroups, availableRescues, unclaimedRescuesGroups } = filteredAvailableRescues.reduce(
    (acc, curr) => {
      const rescueValue = isRescueClaimed(curr) ? 0 : 1;

      const isRescueFromTodayOrAfter = moment(curr.date, 'YYYYMMDD').isSameOrAfter(moment(), 'day');
      if (isRescueFromTodayOrAfter) {
        acc.availableRescues.push(curr);

        if (acc.availableRescuesDayGroups[curr.date]) {
          acc.availableRescuesDayGroups[curr.date]++;
          acc.unclaimedRescuesGroups[curr.date] = acc.unclaimedRescuesGroups[curr.date] + rescueValue;
        } else {
          acc.availableRescuesDayGroups[curr.date] = 1;
          acc.unclaimedRescuesGroups[curr.date] = rescueValue;
        }
      }

      return acc;
    },
    {
      availableRescues: [],
      availableRescuesDayGroups: {},
      unclaimedRescuesGroups: {},
    }
  );

  // Sort prio - date > group > pickup time - this ensures that group rescues will appear first for a given day
  const availableFilteredRescuesSorted = orderBy(
    availableRescues,
    [
      (availableRescue) => availableRescue.date,
      (availableRescue) => ((availableRescue?.donation_groups || []).length > 0 ? 0 : 1),
      (availableRescue) => moment(`${availableRescue.date} ${availableRescue.pickup_begin}`, 'YYYYMMDD HH:mm:ss'),
    ],
    ['asc', 'asc']
  );
  const availableFilteredRescuesSortedCount = availableFilteredRescuesSorted.length;
  const availableRescuesCount = availableRescues.length;

  const groupCounts = Object.values(availableRescuesDayGroups);
  const groupBy = Object.keys(availableRescuesDayGroups);

  const isInflight =
    rescuesEntities.inflight || rescuerAvailableRescues.inflight || rescuerAvailableRescues.lastUpdated === null;

  const onAdoptClick = rescue => history.push(generatePath(routes.rescueAdoptConfirmation, { rescueId: rescue.id }));

  useEffect(() => {
    if (
      location.state &&
      location.state.adoptButton &&
      matchPath(location.state.prevPath, {
        path: routes.rescueAdoptConfirmation,
        exact: true,
      })
    ) {
      const { state } = location;
      history.replace(location.pathname, null); //remove rescueAdoptConfirmation

      if (state.adoptButton === RESCUE_BTN_ADOPTED) {
        const toastButton = {
          color: 'primary',
          variant: 'contained',
          label: 'Undo',
          onClick: () =>
            rescueUnadopt(
              {
                id: state.rescueId,
                pickup_spec_id: state.pickupSpecId,
              },
              true
            ),
        };

        return snackbarHelper.success('Rescue adopted', true, toastButton);
      }
    }
  }, []);

  useEffect(() => {
    setToursEnabled(false);
    if (!isInflight && showInitialInfiniteScrollTour) {
      setToursEnabled(true);
    }
  }, [isInflight, showInitialInfiniteScrollTour]);

  // iOS tour fix
  useEffect(() => {
    if (toursEnabled && Capacitor.getPlatform() === 'ios') {
      const introJSTooltip = document.querySelector('.introjs-tooltip');
      introJSTooltip.style.visibility = 'hidden';
      introJSTooltip.style.transition = 'opacity 0.1s ease-out';
      if (introJSTooltip) {
        setTimeout(() => {
          introJSTooltip.style.opacity = '0';
          introJSTooltip.style.visibility = 'visible';
          introJSTooltip.style.opacity = '1';
        }, 500);
      }
    }
  }, [toursEnabled]);

  const handleRescueClaim = rescue =>
    Bluebird.try(() => setRescuesInflight({ ...rescuesInflight, [rescue.id]: 'claim' }))
      .then(() => rescueClaim(rescue, true))
      .finally(() => setRescuesInflight({ ...rescuesInflight, [rescue.id]: null }));

  const isLastPageFetched =
    rescuerAvailableRescues.pagination.currentPage >= rescuerAvailableRescues.pagination.lastPage;
  const onEndReached = () => {
    if (!isInflight && !isLastPageFetched) {
      fetchScheduleTabPaginated(false, true, rescuerAvailableRescues.pagination.currentPage + 1, false);
    }
  };

  useEffect(() => {
    if (filteredAvailableRescues.length === 0) {
      onEndReached();
    }
  }, [filteredAvailableRescues]);

  const renderGroupContent = index => {
    const unclaimedRescuesLeft = unclaimedRescuesGroups[groupBy[index]];
    return (
      <div style={{ paddingBottom: 8 }}>
        <StyledSubHeader
          color={unclaimedRescuesLeft === 0 ? Colors.rescues.claimed.color : Colors.rescues.unclaimed.color}
          text={moment(groupBy[index], 'YYYYMMDD').format('dddd, MMMM Do')}
          subText={
            unclaimedRescuesLeft === 0
              ? formatMessage({ id: 'rescuer-dashboard-mobile.schedule.no-available-rescues' })
              : `${unclaimedRescuesLeft} `
                + `${formatMessage({ id: 'rescuer-dashboard-mobile.schedule.n-rescues-available' })}`
          }
          icon={
            <Avatar
              sx={[
                { borderRadius: '12px', width: 50, height: 50 },
                unclaimedRescuesLeft > 0 && {
                  backgroundColor: Colors.rescues.unclaimed.color,
                  filter: 'brightness(0.9)',
                },
                unclaimedRescuesLeft === 0 && {
                  backgroundColor: Colors.rescues.claimed.color,
                  filter: 'brightness(0.9)',
                },
              ]}
            >
              <EventAvailableIcon />
            </Avatar>
          }
        />
      </div>
    );
  };

  const renderFooter = () => {
    if (isInflight) {
      return <RescueMobileCard isLoading />;
    }
    if (isLastPageFetched) {
      return (
        <Box textAlign="center" pt={2} pb={9}>
          <Typography variant="subtitle2">That’s all for now.</Typography>
          <Typography variant="subtitle2">More rescues will be available tomorrow.</Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <>
      <Steps
        enabled={toursEnabled}
        options={{
          nextToDone: true,
          hidePrev: true,
        }}
        onExit={() => {
          setUIFilter(RESCUER_SCHEDULE_FILTER, {
            ...scheduleFilters,
            showInitialInfiniteScrollTour: { value: false },
          });
          setToursEnabled(false);
        }}
        initialStep={0}
        steps={infiniteScheduleTour}
      />
      <Box
        sx={(theme) => ({
          position: 'relative',
          height: '100%',
          width: '100%',
          overflow: 'auto',
          backgroundColor: 'background.default',
          padding: theme.spacing(1, 1, 0),
          display: 'flex',
          flexDirection: 'column',
        })}
      >
        {!isInflight && rescuesById.length === 0 && <NoRescueCard text="No available rescues." />}

        {!isInflight && rescuesById.length > 0 && availableRescuesCount === 0 && (
          <NoRescueCard text="There are no available rescues that match the current filter criteria." />
        )}

        <Box display="flex" flex={1} flexDirection="column" data-introid="schedule-rescues-container">
          <GroupedVirtuoso
            style={{ width: '100%', height: '100%' }}
            totalCount={availableFilteredRescuesSortedCount}
            endReached={onEndReached}
            groupCounts={groupCounts}
            groupContent={renderGroupContent}
            components={{
              Footer: renderFooter,
            }}
            itemContent={index => {
              const rescue = availableFilteredRescuesSorted[index];
              const top = index === 0 ? 0 : 1;
              const bottom = index === availableRescuesCount ? 0 : 1;
              const isClaimed = isRescueClaimed(rescue);
              const claimer = getRescueClaimer(rescue);
              const rescuerIsRescueClaimer = activeUser.id === claimer?.id;
              return (
                <Box pt={top} pb={bottom}>
                  <RescueMobileCard
                    key={rescue.id}
                    rescue={rescue}
                    isClaimed={isClaimed}
                    isCancelled={rescue.cancelled_by_id !== null}
                    hideExpandPanel={isClaimed}
                    hideLocations={isClaimed && !rescuerIsRescueClaimer}
                    isPinned={(rescue?.donation_groups || []).length > 0}
                  >
                    <Box display="grid" gap={1} data-introid="claim-rescue-buttons">
                      {showClaimRescueButton(rescue) && (
                        <ButtonWithLoading
                          fullWidth
                          startIcon={<PanToolIcon />}
                          isLoading={rescuesInflight[rescue.id] && rescuesInflight[rescue.id] === 'claim'}
                          disabled={!!rescuesInflight[rescue.id] || !!getRescuerSitePausedAt(activeUser, rescue.site_id)}
                          variant="contained"
                          size="large"
                          onClick={() => handleRescueClaim(rescue)}
                        >
                          {formatMessage({ id: 'rescuer-dashboard-mobile.buttons.i-will-do-it' })}
                        </ButtonWithLoading>
                      )}
                      {showAdoptRescueButton(rescue) && (
                        <ButtonWithLoading
                          fullWidth
                          startIcon={<FavoriteIcon />}
                          isLoading={rescuesInflight[rescue.id] && rescuesInflight[rescue.id] === 'adopt'}
                          disabled={!!rescuesInflight[rescue.id] || !!getRescuerSitePausedAt(activeUser, rescue.site_id)}
                          variant="contained"
                          size="large"
                          onClick={() => onAdoptClick(rescue)}
                        >
                          {formatMessage({ id: 'rescuer-dashboard-mobile.buttons.adopt' })}
                        </ButtonWithLoading>
                      )}
                    </Box>
                  </RescueMobileCard>
                </Box>
              );
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default RescuerScheduleMobileWithInfiniteScroll;
